import i18n, { onLanguageChange } from '../i18n';

// Definizione delle chiavi di traduzione
export const systemKeys = {
  // Categorie principali
  "Stage": "systems.stage",
  "Sound System": "systems.soundSystem",
  "Lights": "systems.lights",
  "Video & Effects": "systems.videoEffects",
  "Backline": "systems.backline",
  
  // Sottocategorie
  "Stage Type": "systems.stageType",
  "Stage Equipment": "systems.stageEquipment",
  "PA System": "systems.paSystem",
  "Mixing Console": "systems.mixingConsole",
  "Microphones": "systems.microphones",
  "Stage Lighting": "systems.stageLighting",
  "Effects": "systems.effects",
  "Control": "systems.control",
  "Screens": "systems.screens",
  "Equipment": "systems.equipment",
  "Amplification": "systems.amplification",
  "Instruments": "systems.instruments",
  "Accessories": "systems.accessories",
  
  // Elementi specifici
  "No Stage": "systems.noStage",
  "Small Stage (up to 4 performers)": "systems.smallStage",
  "Medium Stage (5-8 performers)": "systems.mediumStage",
  "Large Stage (8+ performers)": "systems.largeStage",
  "Modular Stage": "systems.modularStage",
  "Stage Monitors": "systems.stageMonitors",
  "Stage Boxes": "systems.stageBoxes",
  "Power Distribution": "systems.powerDistribution",
  "Stage Effects": "systems.stageEffects",
  "No PA System": "systems.noPaSystem",
  "Basic PA (2 speakers)": "systems.basicPa",
  "Full Range PA": "systems.fullRangePa",
  "Line Array System": "systems.lineArraySystem",
  "Subwoofers": "systems.subwoofers",
  "Analog Mixer": "systems.analogMixer",
  "Digital Mixer": "systems.digitalMixer",
  "In-Ear Monitor System": "systems.inEarMonitorSystem",
  "Multi-track Recording": "systems.multiTrackRecording",
  "Vocal Mics": "systems.vocalMics",
  "Instrument Mics": "systems.instrumentMics",
  "Drum Mic Kit": "systems.drumMicKit",
  "Wireless Systems": "systems.wirelessSystems",
  "Basic Stage Lights": "systems.basicStageLights",
  "LED Par Lights": "systems.ledParLights",
  "Moving Heads": "systems.movingHeads",
  "Follow Spots": "systems.followSpots",
  "UV Lights": "systems.uvLights",
  "Strobe Lights": "systems.strobeLights",
  "Laser Systems": "systems.laserSystems",
  "LED Bars": "systems.ledBars",
  "LED Matrix": "systems.ledMatrix",
  "DMX Controller": "systems.dmxController",
  "Light Console": "systems.lightConsole",
  "Software Control": "systems.softwareControl",
  "Projection Screen": "systems.projectionScreen",
  "LED Wall": "systems.ledWall",
  "Multiple Screens": "systems.multipleScreens",
  "Video Mapping": "systems.videoMapping",
  "Projector": "systems.projector",
  "Media Server": "systems.mediaServer",
  "Video Mixer": "systems.videoMixer",
  "Cameras": "systems.cameras",
  "Fog Machine": "systems.fogMachine",
  "Haze Machine": "systems.hazeMachine",
  "CO2 Jets": "systems.co2Jets",
  "Confetti System": "systems.confettiSystem",
  "Guitar Amps": "systems.guitarAmps",
  "Bass Amps": "systems.bassAmps",
  "Keyboard Amp": "systems.keyboardAmp",
  "Acoustic Amp": "systems.acousticAmp",
  "Drum Kit": "systems.drumKit",
  "Digital Piano": "systems.digitalPiano",
  "Guitar/Bass": "systems.guitarBass",
  "DJ Equipment": "systems.djEquipment",
  "DI Boxes": "systems.diBoxes",
  "Cables & Stands": "systems.cablesStands",
  "Drum Throne/Hardware": "systems.drumThroneHardware"
};

// Crea il dizionario inverso per cercare le chiavi dai valori tradotti
export const createReverseSystemKeyMap = () => {
  const reverseMap: Record<string, string> = {};
  
  // Per ogni lingua supportata, crea mappature per ogni chiave
  ['it', 'en'].forEach(lang => {
    // Salva la lingua corrente
    const currentLang = i18n.language;
    // Cambia temporaneamente la lingua per ottenere le traduzioni
    i18n.changeLanguage(lang);
    
    // Crea le mappature per questa lingua
    Object.entries(systemKeys).forEach(([englishKey, translationKey]) => {
      const translatedValue = i18n.t(translationKey);
      // La chiave nel reverseMap è il valore tradotto, il valore è la chiave di traduzione
      reverseMap[translatedValue] = translationKey;
    });
    
    // Ripristina la lingua originale
    i18n.changeLanguage(currentLang);
  });
  
  return reverseMap;
};

// Cache per la mappa inversa
let reverseSystemKeyMapCache: Record<string, string> | null = null;

// Funzione per ottenere la mappa inversa (con cache)
export const getReverseSystemKeyMap = () => {
  if (!reverseSystemKeyMapCache) {
    reverseSystemKeyMapCache = createReverseSystemKeyMap();
  }
  return reverseSystemKeyMapCache;
};

// Resetta la cache quando cambia la lingua
if (typeof window !== 'undefined') {
  onLanguageChange(() => {
    reverseSystemKeyMapCache = null;
  });
}

// Funzione per convertire il valore tradotto nella chiave di traduzione
export const getSystemKeyFromValue = (translatedValue: string): string => {
  const reverseMap = getReverseSystemKeyMap();
  return reverseMap[translatedValue] || translatedValue;
};

// Funzione per convertire la chiave di traduzione nel valore tradotto
export const getTranslatedSystemValue = (translationKey: string): string => {
  return i18n.t(translationKey, { defaultValue: translationKey });
};

// Funzione helper per tradurre
const t = (key: string): string => {
  return i18n.t(key, { defaultValue: key.split('.').pop() });
};

// Funzione per ottenere i sistemi tradotti
export const getTranslatedSystems = () => {
  return {
    [t(systemKeys["Stage"])]: {
      [t(systemKeys["Stage Type"])]: [
        t(systemKeys["No Stage"]),
        t(systemKeys["Small Stage (up to 4 performers)"]),
        t(systemKeys["Medium Stage (5-8 performers)"]),
        t(systemKeys["Large Stage (8+ performers)"]),
        t(systemKeys["Modular Stage"])
      ],
      [t(systemKeys["Stage Equipment"])]: [
        t(systemKeys["Stage Monitors"]),
        t(systemKeys["Stage Boxes"]),
        t(systemKeys["Power Distribution"]),
        t(systemKeys["Stage Lighting"]),
        t(systemKeys["Stage Effects"])
      ]
    },
    [t(systemKeys["Sound System"])]: {
      [t(systemKeys["PA System"])]: [
        t(systemKeys["No PA System"]),
        t(systemKeys["Basic PA (2 speakers)"]),
        t(systemKeys["Full Range PA"]),
        t(systemKeys["Line Array System"]),
        t(systemKeys["Subwoofers"])
      ],
      [t(systemKeys["Mixing Console"])]: [
        t(systemKeys["Analog Mixer"]),
        t(systemKeys["Digital Mixer"]),
        t(systemKeys["In-Ear Monitor System"]),
        t(systemKeys["Multi-track Recording"])
      ],
      [t(systemKeys["Microphones"])]: [
        t(systemKeys["Vocal Mics"]),
        t(systemKeys["Instrument Mics"]),
        t(systemKeys["Drum Mic Kit"]),
        t(systemKeys["Wireless Systems"])
      ]
    },
    [t(systemKeys["Lights"])]: {
      [t(systemKeys["Stage Lighting"])]: [
        t(systemKeys["Basic Stage Lights"]),
        t(systemKeys["LED Par Lights"]),
        t(systemKeys["Moving Heads"]),
        t(systemKeys["Follow Spots"]),
        t(systemKeys["UV Lights"])
      ],
      [t(systemKeys["Effects"])]: [
        t(systemKeys["Strobe Lights"]),
        t(systemKeys["Laser Systems"]),
        t(systemKeys["LED Bars"]),
        t(systemKeys["LED Matrix"])
      ],
      [t(systemKeys["Control"])]: [
        t(systemKeys["DMX Controller"]),
        t(systemKeys["Light Console"]),
        t(systemKeys["Software Control"])
      ]
    },
    [t(systemKeys["Video & Effects"])]: {
      [t(systemKeys["Screens"])]: [
        t(systemKeys["Projection Screen"]),
        t(systemKeys["LED Wall"]),
        t(systemKeys["Multiple Screens"]),
        t(systemKeys["Video Mapping"])
      ],
      [t(systemKeys["Equipment"])]: [
        t(systemKeys["Projector"]),
        t(systemKeys["Media Server"]),
        t(systemKeys["Video Mixer"]),
        t(systemKeys["Cameras"])
      ],
      [t(systemKeys["Effects"])]: [
        t(systemKeys["Fog Machine"]),
        t(systemKeys["Haze Machine"]),
        t(systemKeys["CO2 Jets"]),
        t(systemKeys["Confetti System"])
      ]
    },
    [t(systemKeys["Backline"])]: {
      [t(systemKeys["Amplification"])]: [
        t(systemKeys["Guitar Amps"]),
        t(systemKeys["Bass Amps"]),
        t(systemKeys["Keyboard Amp"]),
        t(systemKeys["Acoustic Amp"])
      ],
      [t(systemKeys["Instruments"])]: [
        t(systemKeys["Drum Kit"]),
        t(systemKeys["Digital Piano"]),
        t(systemKeys["Guitar/Bass"]),
        t(systemKeys["DJ Equipment"])
      ],
      [t(systemKeys["Accessories"])]: [
        t(systemKeys["DI Boxes"]),
        t(systemKeys["Cables & Stands"]),
        t(systemKeys["Power Distribution"]),
        t(systemKeys["Drum Throne/Hardware"])
      ]
    }
  } as const;
};

// Inizializza i sistemi con i valori tradotti
export let venueSystems = getTranslatedSystems();

// Registra un callback per aggiornare i sistemi quando cambia la lingua
if (typeof window !== 'undefined') {
  onLanguageChange(() => {
    venueSystems = getTranslatedSystems();
  });
}

export type SystemCategory = keyof typeof venueSystems;
export type SystemSubCategory<T extends SystemCategory> = keyof typeof venueSystems[T];
export type SystemItem<T extends SystemCategory, S extends SystemSubCategory<T>> = typeof venueSystems[T][S][number];

export interface VenueEquipment {
  category: SystemCategory;
  subCategory: string;
  items: string[];
  itemIds?: number[]; // ID degli equipment, usati per l'edit mode
} 